import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { roleLabel, workTypeLabel, statusChip, moneyTry } from '../utils/staffFormatters';

import { OverviewPanel } from './detail/panels/OverviewPanel';
import { ProgramPanel } from './detail/panels/ProgramPanel';
import { IncomeKpiPanel } from './detail/panels/IncomeKpiPanel';
import { PermissionsPanel } from './detail/panels/PermissionsPanel';
import { LogsPanel } from './detail/panels/LogsPanel';
import { TabPanel } from 'shared/ui/soft/TabPanel';

export function StaffDetailModal({ open, staff, onClose, onEdit, onUpdatePermissions }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = React.useState(0);
  const [localPerms, setLocalPerms] = React.useState({});

  React.useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  React.useEffect(() => {
    setLocalPerms(staff?.permissions || {});
  }, [staff]);

  if (!open || !staff) return null;

  const st = statusChip(staff.active);

  const metrics = staff.metrics || {
    weeklyRevenue: 50000,
    weeklyCost: 27000,
    memberCount: 38,
    wagePreview:
      staff.workType === 'PERCENT'
        ? 'Yüzde: 35%'
        : staff.workType === 'HOURLY'
        ? 'Saatlik: ₺450'
        : 'Maaş: ₺35.000',
  };

  const weeklySchedule = staff.weeklySchedule || [
    { day: 'Pzt', slots: ['10:00 Reformer', '13:00 Private', '18:00 Grup'] },
    { day: 'Sal', slots: ['11:00 Grup', '17:00 Private'] },
    { day: 'Çar', slots: ['10:00 Reformer', '19:00 Grup'] },
    { day: 'Per', slots: ['12:00 Private', '18:00 Grup'] },
    { day: 'Cum', slots: ['09:00 Grup', '16:00 Reformer'] },
    { day: 'Cts', slots: ['12:00 Grup'] },
    { day: 'Paz', slots: ['—'] },
  ];

  const logs = staff.logs || [
    {
      type: 'PAYMENT',
      title: 'Ödeme aldı',
      meta: 'Üye: Ayşe Yılmaz • ₺1.200 • 2 saat önce',
      amount: 1200,
    },
    { type: 'MEMBER_ADD', title: 'Üye ekledi', meta: 'Yeni üye: Ece Demir • 1 gün önce' },
    { type: 'SESSION_ADD', title: 'Seans ekledi', meta: 'Reformer • Salı 17:00 • 3 gün önce' },
  ];

  const payLine =
    staff.workType === 'FULL_TIME'
      ? `Maaş: ${moneyTry(staff.monthlySalary)}`
      : staff.workType === 'HOURLY'
      ? `Saatlik: ${moneyTry(staff.hourlyRate)}`
      : `Yüzde: ${staff.percentRate ?? '-'}%`;

  const revenueTone = 'bad';
  const costTone = 'good';

  const headerBg = 'linear-gradient(180deg, rgba(0,150,136,.16), rgba(0,150,136,.06))';
  const paperMax = isMobile ? '100%' : 1120;

  const setPerm = (key, nextVal) => {
    const next = { ...(localPerms || {}), [key]: nextVal };
    setLocalPerms(next);
    onUpdatePermissions?.(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
          maxWidth: paperMax,
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox sx={{ minWidth: 0 }}>
              <MDBox display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  {staff.fullName}
                </MDTypography>

                <Chip
                  size="small"
                  icon={
                    <Icon
                      sx={{
                        fontSize: 16,
                        color:
                          st.color === 'success'
                            ? theme.palette.success.main
                            : theme.palette.text.secondary,
                      }}
                    >
                      {st.icon}
                    </Icon>
                  }
                  label={st.label}
                  variant="outlined"
                  sx={{
                    borderRadius: '999px',
                    fontWeight: 800,
                    borderColor: 'rgba(0,150,136,.30)',
                    background: 'rgba(0,150,136,.05)',
                  }}
                />
              </MDBox>

              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ color: theme.palette.text.secondary, mt: 0.25 }}
              >
                {roleLabel(staff.role)} • {workTypeLabel(staff.workType)} • {payLine}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                sx={{ borderRadius: '14px' }}
                onClick={() => onEdit(staff)}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>edit</Icon> Düzenle
              </MDButton>

              <Tooltip title="Kapat">
                <IconButton onClick={onClose}>
                  <Icon sx={{ color: theme.palette.text.primary }}>close</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox sx={{ px: 2, pt: 1.1, background: theme.palette.background.paper }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
          >
            <Tab label="Genel" />
            <Tab label="Program" />
            <Tab label="Gelir & KPI" />
            <Tab label="Yetkiler" />
            <Tab label="Loglar" />
          </Tabs>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ minHeight: isMobile ? 'auto' : 560 }}>
          <TabPanel value={tab} index={0}>
            <OverviewPanel
              active={tab === 0}
              staff={staff}
              weeklySchedule={weeklySchedule}
              metrics={metrics}
              revenueTone={revenueTone}
              costTone={costTone}
              onGoLogs={() => setTab(4)}
            />
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <ProgramPanel
              active={tab === 1}
              trainerId={staff?.id} // ✅ staff trainerId
              day={null} // opsiyonel
              status={null} // opsiyonela
              onSessionClick={(s) => {
                // istersen seans detayı aç
                console.log('clicked session', s);
              }}
            />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <IncomeKpiPanel active={tab === 2} metrics={metrics} />
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <PermissionsPanel active={tab === 3} localPerms={localPerms} onTogglePerm={setPerm} />
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <LogsPanel active={tab === 4} logs={logs} />
          </TabPanel>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}
